import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	useLoaderData,
	ScrollRestoration,
} from '@remix-run/react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { Confetti } from './components/confetti.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { Footer } from './components/footer.tsx'
import {
	NavigationProvider,
	NavigationWrapper,
} from './components/navigation/index.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { StarryBackground } from './components/starry-background.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { useTheme } from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getConfetti } from './utils/confetti.server.ts'
import { prisma } from './utils/db.server.ts'
import { getAllEducationSlugs } from './utils/education/shared/education.server.ts'
import { getEnv } from './utils/env.server.ts'
import { getGuestUser } from './utils/guest-auth.server.ts'
import { getGuestSession } from './utils/guest-session.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { getMetaTitle } from './utils/meta.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { getAllWorkSlugs } from './utils/work/read.server.ts'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? getMetaTitle() : getMetaTitle('Error') },
		{ name: 'description', content: `Nico's Personal Page` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const { confettiId, headers: confettiHeaders } = getConfetti(request)
	const honeyProps = honeypot.getInputProps()

	// Always fetch navigation slugs for consistency
	const workNavigation = await time(() => getAllWorkSlugs(), {
		timings,
		type: 'getAllWorkSlugs',
		desc: 'get work slugs for navigation',
	})

	const educationNavigation = await time(() => getAllEducationSlugs(), {
		timings,
		type: 'getAllEducationSlugs',
		desc: 'get education slugs for navigation',
	})

	// Check for guest user session
	let guestUser = null
	try {
		const guestSession = await getGuestSession(request)
		const guestUserId = guestSession.get('guestUserId')
		const verifiedAt = guestSession.get('verifiedAt')

		if (guestUserId && verifiedAt) {
			// Check if session is not expired (7 days)
			const verificationTime = new Date(verifiedAt)
			const expirationTime = new Date(
				verificationTime.getTime() + 7 * 24 * 60 * 60 * 1000,
			)

			if (new Date() <= expirationTime) {
				guestUser = await getGuestUser(guestUserId)
			}
		}
	} catch {
		// Guest session invalid or expired - ignore and continue
	}

	return json(
		{
			user,
			guestUser,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			confettiId,
			honeyProps,
			workNavigation,
			educationNavigation,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				confettiHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	allowIndexing = true,
}: Readonly<{
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}>) {
	return (
		<html lang="en" className={`${theme}`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const theme = useTheme()
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	useToast(data.toast)

	return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<NavigationProvider>
				<StarryBackground />
				<NavigationWrapper />

				{/* Main content */}

				{/* Main content */}
				<div className="backdrop-blur-md/10 mt-4 flex min-h-screen flex-col items-center">
					<main className="relative z-10 flex justify-center">
						<Outlet />
					</main>
					<Footer />
				</div>

				<EpicToaster closeButton position="top-center" theme={theme} />
				<EpicProgress />
				<Confetti id={data.confettiId} />
			</NavigationProvider>
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

export const ErrorBoundary = GeneralErrorBoundary
